import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { getOpeningHours } from '~/helpers/orchestration/customerService';
import {
  BodyLink,
  MailIcon,
  PhoneIcon,
  TinyTitleBold
} from '@kvdbil/components';
import { requestErrorHandler } from '~/helpers/notifyError';
import { useTranslation } from '~/Locale';
import { OpeningHour } from '../interfaces/OpeningHours';
import styled from 'styled-components';
import {
  formatPhoneNumber,
  formatPhoneNumberForLink
} from '~/helpers/phoneNumber';
import { BodyTextSkeleton } from '~/App/shared/components/Skeletons';

dayjs.extend(calendar);

const ListItem = styled(BodyLink).attrs({ as: 'li' })`
  display: flex;
  justify-content: space-between;
`;

const Cell = styled.div<{ textAlign?: 'left' | 'right'; isAlert?: boolean }>`
  display: flex;
  color: ${({ isAlert, theme }) =>
    isAlert ? theme.colors.error.mainAlt : theme.colors.grayscaleToned.dark5};
  width: 100%;
  padding: 0.625em 0;
  border-bottom: ${({ theme }) =>
    `1px solid ${theme.colors.grayscaleToned.light3}`};
  justify-content: ${({ textAlign }) => textAlign || 'left'};
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${({ theme }) => theme.colors.grayscaleToned.dark5};
  }
`;

const IconLink = styled.a`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.info.dark};
`;

interface Props {
  days?: number;
  phone?: string;
  email?: string;
  onlyDays?: boolean;
}

const WeeklyCustomerService = ({
  days: amountDays,
  phone,
  email,
  onlyDays
}: Props) => {
  const { t } = useTranslation();
  const [days, setDays] = useState<OpeningHour[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const currentDate = dayjs().format('YYYY-MM-DD');

    getOpeningHours(currentDate, amountDays)
      .then(data => {
        if (mounted && data?.length >= 0) {
          setDays(data);
        }
      })
      .catch(requestErrorHandler)
      .finally(() => setIsLoading(false));

    return () => {
      mounted = false;
    };
  }, [amountDays]);

  const calendarFormats = {
    sameDay: `[${t('Today')}]`,
    nextDay: `[${t('Tomorrow')}]`,
    nextWeek: 'dddd',
    sameElse: 'YYYY-MM-DD'
  };

  const daysListItems = (
    <>
      {isLoading &&
        new Array(amountDays ?? 8).fill('').map((_, index) => {
          return (
            <ListItem key={`skeleton-${index}`}>
              <Cell>
                <BodyTextSkeleton height={1.5} style={{ width: '100%' }} />
              </Cell>
            </ListItem>
          );
        })}

      {!isLoading &&
        days &&
        days?.length >= 0 &&
        days.map(day => (
          <ListItem key={day.date.toISOString()}>
            <Cell>
              {dayjs.isDayjs(day.date)
                ? `${day.date.calendar(
                    null,
                    calendarFormats
                  )}, ${day.date.format('D/M')}`
                : null}
            </Cell>
            {day.type === 'CLOSED' ? (
              <Cell textAlign="right" isAlert>
                {t('Customer Service Closed')}
              </Cell>
            ) : (
              <Cell textAlign="right">
                {dayjs.isDayjs(day.open) && dayjs.isDayjs(day.close)
                  ? `${day.open.format('HH:mm')} - ${day.close.format('HH:mm')}`
                  : null}
              </Cell>
            )}
          </ListItem>
        ))}
    </>
  );

  if (onlyDays) {
    return daysListItems;
  }

  return (
    <div>
      <TinyTitleBold>{t('Customer Service')}</TinyTitleBold>
      <div>
        <ul>
          <ListItem>
            <Cell>
              <PhoneIcon />
              <IconLink
                href={`tel:${formatPhoneNumberForLink(
                  phone ?? t('CUSTOMER_CONTACT_PHONE')
                )}`}
              >
                <span>
                  {formatPhoneNumber(phone ?? t('CUSTOMER_CONTACT_PHONE'))}
                </span>
              </IconLink>
            </Cell>
          </ListItem>

          <ListItem>
            <Cell>
              <MailIcon />
              <IconLink href={`mailto:${email ?? t('CUSTOMER_CONTACT_MAIL')}`}>
                <span>{email ?? t('CUSTOMER_CONTACT_MAIL')}</span>
              </IconLink>
            </Cell>
          </ListItem>

          {daysListItems}
        </ul>
      </div>
    </div>
  );
};

export default WeeklyCustomerService;
